import { useEffect, useState } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

/**
 * Returns the set of emails (lowercased) of users that are in the current
 * user's recursive reporting hierarchy, including themselves.
 * `enabled` controls whether the fetch runs.
 */
export const useManagerHierarchyEmails = (user: User | null, enabled: boolean) => {
  const [emails, setEmails] = useState<Set<string> | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (!enabled || !user?.id) {
        setEmails(null);
        return;
      }
      try {
        setLoading(true);
        const { data: subRows, error: subErr } = await supabase
          .rpc('get_user_subordinates', { supervisor_uuid: user.id });
        if (subErr) {
          console.error('useManagerHierarchyEmails: subordinates error', subErr);
        }
        const subIds: string[] = subRows ? subRows.map((r: any) => r.subordinate_id) : [];
        const allIds = Array.from(new Set([user.id, ...subIds]));

        const { data: userRows, error: usrErr } = await supabase
          .from('users')
          .select('id, email')
          .in('id', allIds);
        if (usrErr) {
          console.error('useManagerHierarchyEmails: users error', usrErr);
        }
        const set = new Set<string>();
        (userRows || []).forEach((u: any) => {
          if (u?.email) set.add(String(u.email).toLowerCase());
        });
        // Always include caller email as fallback
        if (user.email) set.add(user.email.toLowerCase());
        setEmails(set);
      } catch (e) {
        console.error('useManagerHierarchyEmails error', e);
        setEmails(new Set(user.email ? [user.email.toLowerCase()] : []));
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [user?.id, user?.email, enabled]);

  return { allowedEmails: emails, loading };
};
